"use client";

import {
  Button as UiButton,
  ErrorFallback,
  LoadingSpinner,
  Modal as UiModal,
  SelectInput,
  StatusBadge,
  type ActionSize,
  type ActionVariant,
  type StatusBadgeVariant,
} from "@cherserg/ui";
import type { ChipVariant } from "../domain";
import { cn } from "./cn";
import { useTableLabelFn } from "./labels-context";
import type {
  TableBadgeProps,
  TableButtonProps,
  TableButtonSize,
  TableButtonVariant,
  TableComponents,
  TableEmptyStateProps,
  TableErrorStateProps,
  TableLoadingProps,
  TableModalProps,
  TableSelectProps,
} from "./table-components";

const BUTTON_VARIANT: Record<TableButtonVariant, ActionVariant> = {
  default: "primary",
  destructive: "danger",
  outline: "secondary",
  secondary: "secondary",
  ghost: "ghost",
  link: "ghost",
};

const BUTTON_SIZE: Record<TableButtonSize, ActionSize> = {
  default: "md",
  lg: "md",
  sm: "sm",
  icon: "sm",
};

const BADGE_VARIANT: Record<ChipVariant, StatusBadgeVariant> = {
  default: "neutral",
  secondary: "neutral",
  destructive: "danger",
  emerald: "success",
  outline: "neutral",
};

function DefaultButton({ variant, size, ...rest }: TableButtonProps) {
  return (
    <UiButton
      variant={BUTTON_VARIANT[variant ?? "default"]}
      size={BUTTON_SIZE[size ?? "default"]}
      {...rest}
    />
  );
}

function DefaultModal({ open, onClose, title, children, maxWidth }: TableModalProps) {
  const t = useTableLabelFn();
  return (
    <UiModal
      open={open}
      onClose={onClose}
      title={title ?? ""}
      hideHeader={title === undefined}
      closeAriaLabel={t("closeDialog")}
      {...(maxWidth !== undefined ? { maxWidthClass: maxWidth } : {})}
    >
      {children}
    </UiModal>
  );
}

/** Нативный select: свойство `searchable` игнорируется. */
function DefaultSelect({
  value,
  onChange,
  options,
  placeholder,
  ariaLabel,
  className,
  disabled,
}: TableSelectProps) {
  const hasEmptyOption = options.some((o) => o.value === "");
  return (
    <SelectInput
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      disabled={disabled}
      className={className ?? ""}
    >
      {placeholder !== undefined && !hasEmptyOption ? (
        <option value="" disabled>
          {placeholder}
        </option>
      ) : null}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </SelectInput>
  );
}

function DefaultBadge({ variant, children, ...rest }: TableBadgeProps) {
  return (
    <StatusBadge variant={BADGE_VARIANT[variant ?? "default"]} {...rest}>
      {children}
    </StatusBadge>
  );
}

function DefaultEmptyState({ message, className, children }: TableEmptyStateProps) {
  return (
    <div
      role="status"
      className={cn("flex flex-col items-center justify-center p-8 text-text-secondary", className)}
    >
      <p className="text-sm">{message}</p>
      {children}
    </div>
  );
}

function DefaultLoading({ className }: TableLoadingProps) {
  const t = useTableLabelFn();
  return <LoadingSpinner label={t("loading")} {...(className !== undefined ? { className } : {})} />;
}

function DefaultErrorState({ message, onRetry }: TableErrorStateProps) {
  const t = useTableLabelFn();
  return (
    <ErrorFallback
      message={message}
      retryLabel={t("retry")}
      {...(onRetry !== undefined ? { onRetry } : {})}
    />
  );
}

export const DEFAULT_TABLE_COMPONENTS: TableComponents = {
  Button: DefaultButton,
  Modal: DefaultModal,
  Select: DefaultSelect,
  Badge: DefaultBadge,
  EmptyState: DefaultEmptyState,
  Loading: DefaultLoading,
  ErrorState: DefaultErrorState,
};
